import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dashboardService, { RentabilidadConcepto } from '../services/dashboard.service';
import { formatCurrency } from '../utils/currency';

const RentabilidadConceptos: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [conceptos, setConceptos] = useState<RentabilidadConcepto[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'ingreso' | 'gasto'>('todos');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getRentabilidadConceptos(year);
      setConceptos(data);
    } catch (error) {
      console.error('Error fetching profitability:', error);
      alert('Error al cargar la rentabilidad');
    } finally {
      setLoading(false);
    }
  };

  const getMargenColor = (margen: number) => {
    if (margen >= 50) return 'text-green-600';
    if (margen >= 30) return 'text-yellow-600';
    if (margen >= 0) return 'text-orange-600';
    return 'text-red-600';
  };

  const getMargenBg = (margen: number) => {
    if (margen >= 50) return 'bg-green-50';
    if (margen >= 30) return 'bg-yellow-50';
    if (margen >= 0) return 'bg-orange-50';
    return 'bg-red-50';
  };

  // Filtrar conceptos
  const conceptosFiltrados = conceptos.filter(c => 
    filtroTipo === 'todos' || c.rubroTipo === filtroTipo
  );

  // Top 10 más rentables
  const top10Rentables = conceptosFiltrados.slice(0, 10);

  // Top 10 menos rentables
  const top10MenosRentables = [...conceptosFiltrados].sort((a, b) => a.rentabilidad - b.rentabilidad).slice(0, 10);

  // Datos para gráfico
  const dataGrafico = top10Rentables.map(c => ({
    nombre: c.conceptoNombre.length > 20 ? c.conceptoNombre.substring(0, 20) + '...' : c.conceptoNombre,
    ingresos: c.totalIngresos,
    gastos: c.totalGastos,
    rentabilidad: c.rentabilidad
  }));

  // Calcular totales
  const totales = conceptosFiltrados.reduce((acc, c) => ({
    ingresos: acc.ingresos + c.totalIngresos,
    gastos: acc.gastos + c.totalGastos,
    rentabilidad: acc.rentabilidad + c.rentabilidad
  }), { ingresos: 0, gastos: 0, rentabilidad: 0 });

  const margenTotal = totales.ingresos > 0 ? (totales.rentabilidad / totales.ingresos) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Cargando rentabilidad...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Rentabilidad por Concepto</h1>
            <p className="mt-2 text-sm text-gray-600">
              Análisis de ingresos, gastos y rentabilidad por producto/servicio
            </p>
          </div>
          <div className="flex space-x-4">
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="todos">Todos los Conceptos</option>
              <option value="ingreso">Solo Ingresos</option>
              <option value="gasto">Solo Gastos</option>
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Total Ingresos */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Ingresos</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totales.ingresos)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Gastos */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Gastos</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totales.gastos)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Rentabilidad Total */}
        <div className={`overflow-hidden shadow rounded-lg ${getMargenBg(margenTotal)}`}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`flex items-center justify-center h-12 w-12 rounded-md ${
                  margenTotal >= 30 ? 'bg-green-600' : margenTotal >= 0 ? 'bg-yellow-600' : 'bg-red-600'
                } text-white`}>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Rentabilidad</dt>
                  <dd className={`text-lg font-semibold ${getMargenColor(margenTotal)}`}>
                    {formatCurrency(totales.rentabilidad)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Margen Promedio */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Margen Promedio</dt>
                  <dd className={`text-lg font-semibold ${getMargenColor(margenTotal)}`}>
                    {margenTotal.toFixed(1)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Top 10 */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Top 10 Conceptos Más Rentables</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={dataGrafico} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="nombre" type="category" width={150} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="ingresos" fill="#10B981" name="Ingresos" />
            <Bar dataKey="gastos" fill="#EF4444" name="Gastos" />
            <Bar dataKey="rentabilidad" fill="#3B82F6" name="Rentabilidad" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabla Completa */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Detalle por Concepto</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Concepto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rubro
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ingresos
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gastos
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rentabilidad
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margen %
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {conceptosFiltrados.map((concepto) => (
                <tr key={concepto.conceptoId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {concepto.conceptoCodigo ? `${concepto.conceptoCodigo} - ` : ''}{concepto.conceptoNombre}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {concepto.rubroNombre || 'Sin rubro'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      concepto.rubroTipo === 'ingreso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {concepto.rubroTipo || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {formatCurrency(concepto.totalIngresos)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                    {formatCurrency(concepto.totalGastos)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                    concepto.rentabilidad >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(concepto.rentabilidad)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${getMargenColor(concepto.margen)}`}>
                    {concepto.margen.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={3} className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                  TOTAL:
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-green-600">
                  {formatCurrency(totales.ingresos)}
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-red-600">
                  {formatCurrency(totales.gastos)}
                </td>
                <td className={`px-6 py-3 text-right text-sm font-bold ${
                  totales.rentabilidad >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(totales.rentabilidad)}
                </td>
                <td className={`px-6 py-3 text-right text-sm font-bold ${getMargenColor(margenTotal)}`}>
                  {margenTotal.toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Insights */}
      {conceptosFiltrados.length > 0 && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Más Rentables */}
          <div className="bg-green-50 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-green-900 mb-4">🏆 Top 5 Más Rentables</h3>
            <ul className="space-y-2">
              {top10Rentables.slice(0, 5).map((c, index) => (
                <li key={c.conceptoId} className="flex justify-between items-center">
                  <span className="text-sm text-green-800">
                    {index + 1}. {c.conceptoNombre}
                  </span>
                  <span className="text-sm font-semibold text-green-900">
                    {formatCurrency(c.rentabilidad)} ({c.margen.toFixed(1)}%)
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Menos Rentables */}
          <div className="bg-red-50 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-900 mb-4">⚠️ Top 5 Menos Rentables</h3>
            <ul className="space-y-2">
              {top10MenosRentables.slice(0, 5).map((c, index) => (
                <li key={c.conceptoId} className="flex justify-between items-center">
                  <span className="text-sm text-red-800">
                    {index + 1}. {c.conceptoNombre}
                  </span>
                  <span className="text-sm font-semibold text-red-900">
                    {formatCurrency(c.rentabilidad)} ({c.margen.toFixed(1)}%)
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentabilidadConceptos;
